// Improve Newco's own Google Analytics 4, loaded through our Tag Manager
// container and ONLY after analytics consent. Nothing here runs at page load
// by itself: trackers.js loads it when the stored or live choice allows it.
//
// Events are plain dataLayer pushes, sent by the container's "GA4 events" tag:
// in its Live environment to the production property, and in every other
// environment (staging builds pass VUE_APP_GTM_ENV_AUTH and
// VUE_APP_GTM_ENV_PREVIEW) to the staging property, with cookies prefixed
// "stg". A Google tag loaded by Tag Manager ignores gtag('event') calls, so
// events must go through that tag. The container, including the dataLayer
// keys the tag reads, is defined in scripts/google-api.js in the tracker
// repo; keep the two in step. Page views come from our router, not Google's
// automatic tracking, so the address can be cleaned first.
//
// Plan: docs/plans/analytics-newco-replacement-2026-10.md in the tracker repo.

import { hasAnalytics } from '@/utils/consent';

const GTM_ID = process.env.VUE_APP_GTM_ID || '';
const GTM_ENV_AUTH = process.env.VUE_APP_GTM_ENV_AUTH || '';
const GTM_ENV_PREVIEW = process.env.VUE_APP_GTM_ENV_PREVIEW || '';
const SITE = 'website';
// The only query parameters Google may see: campaign tags. Emails, tokens and
// purchase ids in a URL never leave the browser.
const KEPT_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Event keys are cleared on every push, so no event inherits another's. The
// page keys (page_location, page_referrer, page_title, ignore_referrer) stay
// until the next page view, so every event on a page carries its clean address.
const EVENT_KEYS = ['form', 'method', 'slot', 'payment_type'];
// _ga and _ga_<stream>; on staging stg_ga and stg_ga_<stream> (the container
// sets the cookie prefix "stg").
const GA_COOKIE = /^([A-Za-z0-9]+)?_ga(_[A-Za-z0-9]+)?$/;
const SHARED_DOMAIN = '.improve.online';
// Hosts that hand the visitor back to us mid visit: a page reached from one
// must not credit it as the source of the visit or of the purchase. This is
// GA4's "unwanted referrals" list, kept here because the Admin API cannot set it.
const UNWANTED_REFERRERS = ['checkout.stripe.com'];

let loaded = false;
let lastLocation = '';

function gtag() {
  // Consent commands only. Tag Manager reads the arguments object itself, so
  // this stays a plain function.
  window.dataLayer.push(arguments);
}

export function isAnalyticsLoaded() {
  return loaded;
}

// The page address Google is allowed to see: origin, path and campaign tags.
export function sanitisedLocation(loc) {
  const params = new URLSearchParams(loc.search);
  const kept = new URLSearchParams();
  KEPT_PARAMS.forEach((key) => {
    if (params.has(key)) kept.set(key, params.get(key));
  });
  const query = kept.toString();
  return loc.origin + loc.pathname + (query ? '?' + query : '');
}

// Another site's address may carry its own personal data in the query.
export function sanitisedReferrer(referrer) {
  if (!referrer) return '';
  try {
    const url = new URL(referrer);
    return url.origin + url.pathname;
  } catch (e) {
    return '';
  }
}

function isUnwantedReferrer(referrer) {
  try {
    return UNWANTED_REFERRERS.indexOf(new URL(referrer).hostname) !== -1;
  } catch (e) {
    return false;
  }
}

export function loadAnalytics() {
  if (loaded || !GTM_ID || typeof window === 'undefined' || !hasAnalytics()) return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  // Consent Mode v2, before the container: everything denied by default, then
  // only analytics granted. The ads signals stay denied: we run no advertising.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  });
  gtag('consent', 'update', { analytics_storage: 'granted' });
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const env = GTM_ENV_AUTH
    ? '&gtm_auth=' + encodeURIComponent(GTM_ENV_AUTH) +
      '&gtm_preview=' + encodeURIComponent(GTM_ENV_PREVIEW) + '&gtm_cookies_win=x'
    : '';
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtm.js?id=' + encodeURIComponent(GTM_ID) + env;
  document.head.appendChild(script);
}

// Sends one GA4 event when the visitor allowed analytics. `ecommerce` holds
// value, currency, items and transaction_id, for begin_checkout and purchase.
// Never put a name, an email, a company or an id that unlocks anything in
// either.
export function track(name, params = {}, ecommerce = null) {
  if (!GTM_ID || typeof window === 'undefined' || !hasAnalytics()) return;
  loadAnalytics();
  const event = { event: name, site: SITE, ecommerce: null };
  EVENT_KEYS.forEach((key) => {
    event[key] = undefined;
  });
  Object.assign(event, params);
  if (ecommerce) {
    // Google's pattern: clear the previous basket first, or the new one is
    // merged into it.
    window.dataLayer.push({ ecommerce: null });
    event.ecommerce = ecommerce;
  }
  window.dataLayer.push(event);
}

// After each route change, and once when consent is first given.
export function trackPageView() {
  if (!GTM_ID || typeof window === 'undefined' || !hasAnalytics()) return;
  const location = sanitisedLocation(window.location);
  const referrer = lastLocation || sanitisedReferrer(document.referrer);
  lastLocation = location;
  // ignore_referrer is set or cleared on every page view, so it never
  // outlives the page that came from payment.
  track('page_view', {
    page_location: location,
    page_referrer: referrer,
    page_title: document.title,
    ignore_referrer: isUnwantedReferrer(referrer) || undefined,
  });
}

// Refusal or withdrawal: tell the Google tag, then delete Google's cookies on
// the shared domain and on this host.
export function clearAnalytics() {
  if (typeof document === 'undefined') return;
  if (loaded) gtag('consent', 'update', { analytics_storage: 'denied' });
  document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter((name) => GA_COOKIE.test(name))
    .forEach((name) => {
      document.cookie = name + '=; max-age=0; path=/';
      document.cookie = name + '=; max-age=0; path=/; domain=' + SHARED_DOMAIN;
    });
}

// Google's anonymous id for this browser: the two numbers at the end of the
// _ga cookie (staging's is stg_ga, VUE_APP_GA_COOKIE). The API keeps it with a
// purchase so it can report a bank transfer to Google when it is paid, days
// after the buyer left (analytics plan Phase 3). '' without analytics consent,
// or before Google has set the cookie.
export function gaClientId() {
  if (typeof document === 'undefined' || !hasAnalytics()) return '';
  const name = process.env.VUE_APP_GA_COOKIE || '_ga';
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  const parts = match ? decodeURIComponent(match[1]).split('.') : [];
  return parts.length >= 4 ? parts.slice(-2).join('.') : '';
}

// A one way hash of the purchase id: the raw uuid unlocks the contract
// download, so it must never reach Google. Resolves '' without SubtleCrypto.
export function transactionId(uuid) {
  try {
    const bytes = new TextEncoder().encode(String(uuid));
    return window.crypto.subtle.digest('SHA-256', bytes).then((buf) =>
      Array.from(new Uint8Array(buf))
        .slice(0, 8)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
    );
  } catch (e) {
    return Promise.resolve('');
  }
}
